/**
 * Disconnect a bank (v7.1.0, security roadmap A8).
 *
 * The one destructive control in the app, so the sheet says — before the tap —
 * exactly what goes (this bank's accounts and transactions, from Ballast and at
 * Plaid), what each entity's free money does as a result, what stays (the
 * operator's envelopes, decisions and taught senders), and the one thing Plaid
 * will not give back (the connection slot).
 *
 * The owner's decision (10 Oct 2026): warn and allow. If envelopes will end up
 * holding more than the accounts left, the sheet names the figure; it does not
 * refuse, and it moves nothing on the operator's behalf.
 *
 * Words live in lib/connection-copy.ts, where they are unit-tested.
 */

import { disconnectCopy } from '../lib/connection-copy';
import { trapFocus } from '../lib/dialog-trap';
import type { DisconnectPreview } from '../lib/api';

export interface DisconnectSheetOptions {
  preview: DisconnectPreview;
  /** Disconnect and purge. Rejecting leaves the sheet open with the reason. */
  onConfirm: () => Promise<void>;
  onDismiss: () => void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

export function createDisconnectSheet(opts: DisconnectSheetOptions): HTMLElement {
  const copy = disconnectCopy(opts.preview);

  const backdrop = el('div', 'sheet-backdrop');
  const sheet = el('div', 'sheet disconnect-sheet');
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'true');
  sheet.setAttribute('aria-labelledby', 'disconnect-title');
  sheet.setAttribute('aria-describedby', 'disconnect-removes');

  const title = el('h2', 'sheet-title', copy.title);
  title.id = 'disconnect-title';
  const removes = el('p', 'sheet-sub', copy.removes);
  removes.id = 'disconnect-removes';

  const impact = el('ul', 'disconnect-impact');
  for (const line of copy.impact) {
    const li = el('li', 'impact-line', line.text);
    li.dataset.tone = line.tone;
    impact.append(li);
  }

  const keeps = el('p', 'sheet-note soft', copy.keeps);
  const slot = el('p', 'sheet-note soft', copy.slot);

  const message = el('p', 'sheet-message');
  message.setAttribute('role', 'alert');

  const actions = el('div', 'sheet-actions');
  const cancel = el('button', 'btn-quiet', 'Keep it');
  cancel.type = 'button';
  cancel.addEventListener('click', () => opts.onDismiss());
  const confirm = el('button', 'btn-primary btn-destroy', 'Disconnect and delete');
  confirm.type = 'button';
  actions.append(cancel, confirm);

  confirm.addEventListener('click', async () => {
    confirm.disabled = true;
    cancel.disabled = true;
    confirm.textContent = 'Disconnecting…';
    message.textContent = '';
    try {
      await opts.onConfirm();
    } catch (err) {
      message.textContent =
        err instanceof Error && err.message
          ? err.message
          : "Couldn't disconnect. Nothing was deleted — try again.";
      confirm.textContent = 'Disconnect and delete';
      confirm.disabled = false;
      cancel.disabled = false;
    }
  });

  sheet.append(title, removes);
  if (copy.impact.length) sheet.append(impact);
  sheet.append(keeps, slot, message, actions);
  backdrop.append(sheet);
  trapFocus({ backdrop, sheet, onDismiss: opts.onDismiss });
  // Focus starts on "Keep it": the safe choice is the default one. A microtask,
  // as the other sheets do, so it lands once the caller has appended the sheet.
  queueMicrotask(() => cancel.focus());
  return backdrop;
}
